import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/config';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const REPORT_SECTIONS = [
  ['summary', 'Riepilogo soggiorni'],
  ['bookings', 'Elenco prenotazioni'],
  ['revenue', 'Ricavi e importi proprietario'],
  ['occupancy', 'Tasso di occupazione'],
  ['compliance', 'Invii Questura e ISTAT'],
];

const OWNER_FIELDS = [
  ['owner_name', 'Nome o ragione sociale'],
  ['owner_email', 'Email'],
  ['owner_phone', 'Telefono'],
  ['owner_tax_code', 'Codice fiscale / P. IVA'],
  ['owner_address', 'Indirizzo'],
  ['owner_notes', 'Note'],
];

const currentMonth = () => new Date().toISOString().slice(0, 7);
const monthBounds = () => {
  const now = new Date();
  return {
    from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
    to: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10),
  };
};

export default function HomeSmartPropertyReportDialog({ open, onClose, property }) {
  const [periodType, setPeriodType] = useState('month');
  const [month, setMonth] = useState(currentMonth());
  const [dateFrom, setDateFrom] = useState(monthBounds().from);
  const [dateTo, setDateTo] = useState(monthBounds().to);
  const [sections, setSections] = useState(['summary', 'bookings', 'revenue', 'occupancy']);
  const [ownerFields, setOwnerFields] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [brandingMode, setBrandingMode] = useState('home_smart');
  const [whiteLabel, setWhiteLabel] = useState(null);

  const availableOwnerFields = useMemo(
    () => OWNER_FIELDS.filter(([key]) => String(property?.[key] || '').trim()),
    [property],
  );

  useEffect(() => {
    if (!open) return;
    setOwnerFields(availableOwnerFields.map(([key]) => key));
    const token = localStorage.getItem('hw_token');
    axios.get(`${API}/api/home-smart/white-label/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setWhiteLabel(data);
        setBrandingMode(data.active && data.logo_configured ? 'company' : 'home_smart');
      })
      .catch(() => { setWhiteLabel(null); setBrandingMode('home_smart'); });
  }, [open, property, availableOwnerFields]);

  const toggle = (value, checked, setter) => {
    setter((current) => checked ? [...new Set([...current, value])] : current.filter((item) => item !== value));
  };

  const downloadReport = async () => {
    if (!sections.length) {
      toast.error('Seleziona almeno una sezione del report');
      return;
    }
    if (periodType === 'range' && (!dateFrom || !dateTo || dateTo < dateFrom)) {
      toast.error('Controlla le date dell’intervallo');
      return;
    }
    setDownloading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/home-smart/reports/property/${property.id}/pdf`,
        {
          period_type: periodType,
          month: periodType === 'month' ? month : null,
          date_from: periodType === 'range' ? dateFrom : null,
          date_to: periodType === 'range' ? dateTo : null,
          sections,
          owner_fields: property.home_smart_property_role === 'gestore' ? ownerFields : [],
          branding_mode: brandingMode,
        },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
      );
      const disposition = response.headers['content-disposition'] || '';
      const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || 'report-home-smart.pdf';
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success('Report PDF scaricato');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile generare il report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="home-smart-report-dialog">
        <DialogHeader>
          <DialogTitle data-testid="home-smart-report-title">Report PDF — {property?.name || 'Immobile'}</DialogTitle>
          <DialogDescription data-testid="home-smart-report-description">Scegli periodo e contenuti da condividere.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <section className="space-y-3" data-testid="home-smart-report-branding-section">
            <Label>Logo del report</Label>
            <Select value={brandingMode} onValueChange={setBrandingMode}>
              <SelectTrigger data-testid="home-smart-report-branding-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="home_smart" data-testid="home-smart-report-brand-home-smart-option">Logo Home Smart</SelectItem>
                <SelectItem value="company" disabled={!whiteLabel?.active || !whiteLabel?.logo_configured} data-testid="home-smart-report-brand-company-option">Il mio logo</SelectItem>
                <SelectItem value="none" disabled={!whiteLabel?.active} data-testid="home-smart-report-brand-none-option">Nessun logo</SelectItem>
              </SelectContent>
            </Select>
            {!whiteLabel?.active && <p className="text-xs text-slate-500" data-testid="home-smart-report-branding-locked">Il logo aziendale e l’opzione senza logo richiedono White Label.</p>}
          </section>

          <section className="space-y-3" data-testid="home-smart-report-period-section">
            <Label>Periodo</Label>
            <Select value={periodType} onValueChange={setPeriodType}>
              <SelectTrigger data-testid="home-smart-report-period-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month" data-testid="home-smart-report-period-month-option">Mese</SelectItem>
                <SelectItem value="range" data-testid="home-smart-report-period-range-option">Intervallo personalizzato</SelectItem>
              </SelectContent>
            </Select>
            {periodType === 'month' ? (
              <Input type="month" value={month} onChange={(event) => setMonth(event.target.value)} data-testid="home-smart-report-month-input" />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><Label htmlFor="report-date-from">Dal</Label><Input id="report-date-from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} data-testid="home-smart-report-date-from-input" /></div>
                <div><Label htmlFor="report-date-to">Al</Label><Input id="report-date-to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} data-testid="home-smart-report-date-to-input" /></div>
              </div>
            )}
          </section>

          <section className="space-y-3" data-testid="home-smart-report-sections">
            <Label>Sezioni da includere</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {REPORT_SECTIONS.map(([key, label]) => (
                <label key={key} className="flex cursor-pointer items-center gap-3 border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                  <Checkbox checked={sections.includes(key)} onCheckedChange={(checked) => toggle(key, checked, setSections)} data-testid={`home-smart-report-section-${key}`} />
                  {label}
                </label>
              ))}
            </div>
          </section>

          {property?.home_smart_property_role === 'gestore' && (
            <section className="space-y-3" data-testid="home-smart-report-owner-section">
              <div>
                <Label>Dati del proprietario da mostrare</Label>
                <p className="mt-1 text-xs text-slate-500" data-testid="home-smart-report-owner-help">Compaiono solo i dati compilati nella scheda immobile.</p>
              </div>
              {availableOwnerFields.length ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  {availableOwnerFields.map(([key, label]) => (
                    <label key={key} className="flex cursor-pointer items-center gap-3 border border-slate-200 px-3 py-2 text-sm">
                      <Checkbox checked={ownerFields.includes(key)} onCheckedChange={(checked) => toggle(key, checked, setOwnerFields)} data-testid={`home-smart-report-owner-${key}`} />
                      {label}
                    </label>
                  ))}
                </div>
              ) : (
                <p className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="home-smart-report-no-owner-data">Nessun dato proprietario compilato. Puoi aggiungerlo modificando l’immobile.</p>
              )}
            </section>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={onClose} disabled={downloading} data-testid="home-smart-report-cancel-button">Annulla</Button>
            <Button onClick={downloadReport} disabled={downloading || !property} className="bg-blue-700 hover:bg-blue-800" data-testid="home-smart-report-download-button">
              {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Genera e scarica PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}